import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Bell, Receipt, Wallet } from "lucide-react";
import { api, fmt, fmtRange } from "@/lib/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 5000,
  });

  const go = (b) => navigate(`/admin/booking/${b.id}`);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <h1 className="font-display text-2xl">نظرة عامة</h1>
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={CalendarClock} label="حجوزات اليوم" value={data?.today?.length || 0} />
        <Stat icon={Bell} label="طلبات جديدة" value={data?.new_requests?.length || 0} highlight />
        <Stat icon={Receipt} label="تحويلات تنتظر" value={data?.transfers?.length || 0} highlight />
        <Stat icon={Wallet} label="حجوزات عليها متبقٍ" value={data?.remaining?.length || 0} />
      </div>

      <Section title="طلبات الحجز الجديدة" testid="new-requests" items={data?.new_requests} empty="لا توجد طلبات جديدة"
        render={(b) => <BookingRow key={b.id} b={b} onClick={() => go(b)} right={<span className="text-xs text-gold">جديد</span>} />} />

      <Section title="تحويلات تنتظر التأكيد" testid="pending-transfers" items={data?.transfers} empty="لا توجد تحويلات"
        render={(b) => <BookingRow key={b.id} b={b} onClick={() => go(b)} right={<span className="text-xs text-yellow-400">مراجعة الوصل</span>} />} />

      <Section title="حجوزات اليوم" testid="today-bookings" items={data?.today} empty="لا حجوزات اليوم"
        render={(b) => <BookingRow key={b.id} b={b} onClick={() => go(b)} right={<span className="text-xs gold-text">{b.booking_number}</span>} />} />

      <Section title="مبالغ متبقية مهمة" testid="remaining-list" items={data?.remaining} empty="لا مبالغ متبقية"
        render={(b) => <BookingRow key={b.id} b={b} onClick={() => go(b)} right={<span className="text-xs text-red-400">متبقٍ {fmt(b.remaining)}</span>} />} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, highlight }) => (
  <div className={`card-lux p-4 ${highlight && value > 0 ? "ring-1 ring-gold" : ""}`}>
    <Icon size={20} className="text-gold mb-2" />
    <p className="text-2xl font-bold text-offwhite">{value}</p>
    <p className="text-xs text-muted2">{label}</p>
  </div>
);

const Section = ({ title, items, render, empty, testid }) => (
  <div data-testid={testid}>
    <h2 className="text-sm text-muted2 mb-2">{title}</h2>
    <div className="space-y-2">
      {items?.length ? items.map(render) : <p className="text-muted2/60 text-sm card-lux p-4">{empty}</p>}
    </div>
  </div>
);

const BookingRow = ({ b, onClick, right }) => (
  <button onClick={onClick} data-testid={`booking-row-${b.id}`} className="card-lux p-4 w-full flex items-center justify-between hover:bg-navy3/40 transition-colors text-start">
    <div>
      <p className="text-offwhite font-medium">{b.name}</p>
      <p className="text-xs text-muted2">{b.venue_name} · {b.date} · {fmtRange(b.start_time, b.end_time)} · {b.event_type}</p>
    </div>
    {right}
  </button>
);
